import $ from '@nuskin/nuskinjquery';
import {ConfigService, RunConfigService, storage, events, nuskin, UrlService, PersonalOfferStorageService,
    StringService, NsNotQualifiedException, NsQuantityNotAvailable, NsProductNotReleasedForSale} from '@nuskin/ns-util';
import {AdrService, Product, ScanCard, CurrencyService} from '@nuskin/ns-shop';
import {UserService, AccountManager}  from '@nuskin/ns-account';
import ProductService from './productService.js';
import Cart           from './cart.js';
import AdrEditService from '../adr/AdrEditService.js';
import QualificationService from '../qualification/qualificationService.js';
import ProductStatus from './productStatus.js';
import webLoyalty from '@nuskin/ns-loyalty-web';

export default {
    goToCartPage,
    getCartPage,
    goToCheckoutPage,
    getCheckoutPage,
    setCartProperties,
    updateItemPrices,
    checkForMissingEventNames,
    getCartProperty,
    clearCart,
    getCartInfo,
    getPriceTypeTotal,
    getItemData,
    getFirstItemBySku,
    getItemTitleBySku,
    hasItems,
    hasEventItems,
    hasPreviewItems,
    getItemCnt,
    addProductToCart,
    addSkuToCart,
    getAddToCartQty,
    changeItemQty,
    syncCartItemToSapItem,
    removeItemBySku,
    removeItemByKey,
    removeItems,
    toggleItemAdrStatus,
    getOrderSkuQueryParams,
    isBusinessPortfolioAdr,
    setBusinessPortfolioAdr,
    setItemAdrFlags,
    changeBpItem,
    addAgelocMeCodeToCart,
    cloneCart,
    adrHasAgelocMe,
    setSignupMode,
    inSignupMode,
    minAdrPsvMet,
    checkProductLanguages,
    scanQualifiedCnt,
    getScanCards,
    removeScanCard,
    hasScanCardNumber,
    addScanCard,
    updateItemPointsPrice,
    resetPointsAllocations,
    getItemUnitPsv,
    setCartString,
    toggleLoyaltyRewardPoints,
    updateLoyaltyPoints,
    toggleLoyaltyRewardPointsAdr,
    calculateLoyaltyEarnings,
    calculateLoyaltyOrderTotal,
    calculateLoyaltyEarningsToNextIncrement,
    getOrderLoyaltyRewardsPointsJson,
    clearLoyaltyPoints,
    containsAdrItems
}

const CART_STORAGE_KEY = storage.metadata.ECOMM3_CART;
const INVALID_PROP_TYPE_QUERIES = ['object', 'function']

let _cart_;

/**
 *
 * @memberof CartService
 * @example
 * CartService.getCart()
 * The cart will be cleared if runConfig.country does not match the one in the stored cart.
 * The cart will be cleared if the logged in goes from something to nothing (logout)
 * The cart will be cleared if the cart is a pitch cart and the user is not in pitch mode, or vice versa
 * The cart will not be cleared if the logged in goes from nothing to something (login)
 *
 * @returns nuskin.newShop.Cart
 */
function _getCart() {
    if (!_cart_) {
        let runConfig = RunConfigService.getRunConfig(null),
            user = UserService.getUser(),
            userId = user ? user.id : null,
            cartJson = storage.getItem({...CART_STORAGE_KEY, key: _getCartStorageName(runConfig.country)}),
            isPitchApp = PersonalOfferStorageService.isPersonalOffer();

        // If the cartJson userId is empty OR null, then we are going from nothing to something, and we want to keep our items
        if (cartJson &&
            (!UrlService.isLocalePage() || (cartJson.cntryCd === runConfig.country)) &&
            (!cartJson.userId || cartJson.userId === userId) &&
            (!(isPitchApp ^ cartJson.isPitchCart))) {

            if (!cartJson.userId && userId != '') {
                cartJson.userId = userId;
                storage.setItem({...CART_STORAGE_KEY, key: _getCartStorageName(runConfig.country)}, cartJson);
            }
            _cart_ = new Cart(cartJson);
            _cart_.setCartString();
            let modified = _cart_.updateLineItemPrices();
            if (_cart_.setBusinessPortfolioAdr(false) || modified) {
                setCart(_cart_);
            }
        } else {
            _cart_ = new Cart({cntryCd: runConfig.country, userId: userId, isPitchCart: isPitchApp});
            setCart(_cart_);
        }
    }

    return _cart_;
}

/**
 * This writes the good product to local storage (Non SAP I guess). The product key looks something like "01160101|O|N||".
 * toJSON - returns the cart in JSON format
 * @memberof CartService
 * @example
 * CartService.setCart(cart)
 * @returns {object}
 */
function setCart(cart, updateTimestamp = true) {
    if (cart != null && cart instanceof Cart) {
        cart.timestamp = updateTimestamp ? Date.now() : cart.timestamp;
        storage.setItem({...CART_STORAGE_KEY, key: _getCartStorageName(cart.cntryCd)}, cart.toJSON());
    } else {
        if (cart) {
            cart.timestamp = updateTimestamp ? Date.now() : cart.timestamp;
            storage.setItem({...CART_STORAGE_KEY, key: _getCartStorageName(cart.cntryCd)}, cart);
        } else {
            cart = new Cart({cntryCd: RunConfigService.getRunConfig().country, userId: ''});
            storage.setItem({...CART_STORAGE_KEY, key: _getCartStorageName(cart.cntryCd)}, cart);
        }
    }
    _cart_ = cart;
}

/**
 * Gets the name of the cart to use.  The name will either be shop-cart or, if
 * marketConfig.useMarketCarts is on, will be shop-cart-XX where XX is market.
 *
 * @param country
 * @return {string}
 * @private
 */
function _getCartStorageName(country) {
    const nsConfig = ConfigService.getMarketConfig();

    return nsConfig.useMarketCarts
        ? `${CART_STORAGE_KEY.key}-${country}`
        : CART_STORAGE_KEY.key;
}

/**
 * Clears the cart from localStorage.
 *
 * @return {*}
 */
function clearCart() {
    _getCart().removeItems({all: true});  // to trigger events
    setCart(null);
    return _cart_;
}

function setCartProperties(propsJson = {}, updateTimestamp = false) {
    let cart = _getCart();

    Object.keys(propsJson).forEach(key => {
        if (cart.hasOwnProperty(key) &&
            typeof propsJson[key] === typeof cart[key] &&
            !INVALID_PROP_TYPE_QUERIES.find(invalid => typeof cart[key] === invalid)) {

            if (cart[key] !== propsJson[key]) {
                cart[key] = propsJson[key];
            }
        } else {
            console.log(`Could not set property: ${key}`);
        }
    });

    setCart(cart, updateTimestamp);
}

function updateItemPrices() {
    let retVal = false,
        cart = _getCart();

    if (cart.updateLineItemPrices()) {
        setCart(cart);
        retVal = true;
    }
    return retVal;
}

function checkForMissingEventNames(order) {
    const cart = _getCart();
    cart.setMissingEventNames(order);
    setCart(cart);
}

function getCartProperty(key) {
    let cart = _getCart(),
        retVal;

    if (cart.hasOwnProperty(key) && !INVALID_PROP_TYPE_QUERIES.find(invalid => typeof cart[key] === invalid)) {
        retVal = cart[key];
    } else {
        console.log(`Could not get property: ${key}`);
    }
    return retVal;
}

function getItemData(options) {
    return _getCart().getItemData(options);
}

/**
 *  Returns the first item found in the cart based on sku and options
 *
 *  @param sku
 *  @param options
 *  @return {*}
 */
function getFirstItemBySku(sku, options) {
    return _getCart().getFirstItemBySku(sku, options);
}

/**
 * Given a SKU, will search the cart based on optinos and return the item's title
 *
 * @param sku
 * @param options
 * @return string
 */
function getItemTitleBySku(sku, options) {
    return _getCart().getItemTitleBySku(sku, options);
}

function hasItems(options) {
    return _getCart().hasItems(options);
}

function hasEventItems() {
    return _getCart().hasEventItems();
}

function hasPreviewItems() {
    return _getCart().hasPreviewItems();
}

function getItemCnt(options) {
    return _getCart().getItemCnt(options);
}

function getCartInfo(options) {
    return _getCart().getCartInfo(options);
}

function getPriceTypeTotal(priceType) {
    return _getCart().getItems({cartOrderItems: true}).map(item => {
        return item.product.getPricing(priceType) * item.qty;
    }).reduce((a, b) => a + b, 0);
}

/**
 * Adds a product to the cart. If is not a SAP product it checks for excusivity before
 *  adding to the cart.
 *
 * @param options - {
 *              userId: id of logged in user or '',
 *              cntryCd: country code of market,
 *              product: nuskin.newShop.Product,
 *              qty: number of products,
 *              maxQty: maximum qty,
 *              adr: true === product is for adr,
 *              isBusinessPortfolio: true = product is designated as business portfolio,
 *              canDelete: optional, default is true
 *              redeem: default is false
 *              referrer[optional]: contains the referrer that called this function, or in other words, where the product was
 *                  added from (search, productPage, categoryPage, etc.)
 *          }
 * @returns {Promise<void>}
 * @private
 */
async function addProductToCart(options) {
    let cart = _getCart();

    if (options.product.status != ProductStatus.ReleasedForSale) {
        throw new NsProductNotReleasedForSale('This product is not released for sale');
    }

    if (typeof options.qty != 'number') {
        options.qty = parseInt(options.qty);
    }

    if (!options.sapLineId && options.product.isExclusive) {
        let item = cart.getItemData().find(cartItem => cartItem.sku === options.product.sku),
            origQty = item ? item.qty : 0;

        await _checkQualification(options.product.sku, options.qty + origQty);
    }

    let itemKey = cart.addProduct({
        product: options.product,
        qty: options.qty,
        maxQty: options.maxQty,
        adr: options.adr,
        isBusinessPortfolio: options.isBusinessPortfolio,
        canDelete: options.canDelete,
        redeem: options.redeem,
        referrer: options.referrer,
        sapLineId: options.sapLineId,
        qtyRedeemWithPoints: options.qtyRedeemWithPoints,
        loyaltyPointsUsed: options.loyaltyPointsUsed,
        replace: options.replace
    });

    setCart(cart, !cart.getItemByKey(itemKey).isSapLineItem());
}

/**
 * @memberof CartService
 * @example
 * CartService.addSkuToCart(options, callbackFn)
 * options: {
 *      sku: sku to be added to the CART
 *      userId: userId of logged in user
 *      cntryCd: countryCode of market
 *      language: languageCode
 *      qty: quantity
 *      region: for CA
 *      isAdr[optional]: a boolean to indicate if the product isAdr
 *      referrer[optional]: a string indicating where the product was added (search, productPage, categoryPage, etc.)
 *  }
 */
async function addSkuToCart(options) {
    let retVal,
        response = await ProductService.getProductBySku(options);

    if (response.success === true) {
        let product = response.product,
            isAdr = (product.isAdrOnly() || options.isAdr) && !AdrService.isAdrOverride(),
            qty = options.qty || 1,
            maxQty = options.maxQty || 999,
            isPitchCart;

        if (options.adrOnly) {
            product.orderTypes.order = false;
        }
        if (options.isPitchCart) {
            isPitchCart = options.isPitchCart;
        }

        product.lang = options.language;

        // if agelocme, override certain data
        if (options.agelocme && options.agelocme.isCustom) {
            product.agelocme = options.agelocme;
            product.sku = options.agelocme.code;
        }

        // Setup the parameters.
        let itemKey = await addProductToCart({
            userId: options.userId,
            cntryCd: options.cntryCd,
            product: product,
            qty: qty,
            maxQty: maxQty,
            adr: isAdr,
            canDelete: options.canDelete,
            isBusinessPortfolio: options.isBusinessPortfolio,
            redeem: options.redeem,
            isPitchCart: isPitchCart,
            referrer: options.referrer
        });

        retVal = {
            success: true,
            sku: product.sku,
            isAdr: isAdr,
            isBusinessPortfolio: options.isBusinessPortfolio,
            itemKey: itemKey,
            product: product,
            options: options
        };

    } else {
        retVal = response;
    }

    return retVal;
}

/**
 * This is called when adding an agelocme customized lotion to the cart.  In this case the SKU is
 *  retrieved from market configuration to be used in the display of the product in the cart. This
 *  takes the same options as addSkuToCart with
 *  an added required option named agelocme.
 *
 * @param options = {
 *                      <refer to addSkuToCart>
 *                      agelocme: {     This object is passed through and used in the nested callbacks
 *                          code        agelocme code (AB23)
 *                          label       nickname
 *                          name        name of person
 *                      }
 *                  }
 */
async function addAgelocMeCodeToCart(options) {
    let nsConfig = ConfigService.getMarketConfig();

    if (options.agelocme.calibration) {
        options.sku = nsConfig.agelocme.calibrationSku;
    } else if (options.agelocme.isCoreSolution) {
        options.sku = nsConfig.agelocme.coreSku;
    } else {
        options.sku = nsConfig.agelocme.sku;
    }
    return addSkuToCart(options);
}

/**
 * Returns the quantity that is available for this SKU to be added to the cart. This
 * takes into account qualification quantity and quatity already in the cart.
 *
 * @param product - Product object or SKU string
 * @returns retVal  available quantity to add to cart
 */
async function getAddToCartQty(product) {
    let retVal = 0;

    if (typeof product === 'string') {
        let response = await ProductService.getProductBySku({sku: product});
        if (response.success) {
            product = response.product;
        }
    }
    if (product instanceof Product) {
        let cartItem = _getCart().getItemData().find(cartItem => cartItem.sku === product.sku),
            cartQty = cartItem ? cartItem.qty : 0;

        retVal = product.maxQuantity - cartQty;
        if (product.isExclusive) {
            try {
                let qual = await QualificationService.getQualification(product.sku),
                    qualQty = qual && qual.isConsumable && qual.quantity - cartQty > 0
                        ? qual.quantity - cartQty
                        : 0;

                if (qualQty < retVal) {
                    retVal = qualQty;
                }
            } catch (err) {
                retVal = 0;
            }
        }
    }

    return retVal;
}

async function changeItemQty(cartItemInfo, qty = 0, referror) {
    if (qty === 0) {
        // Remove the Product
        removeItemByKey(cartItemInfo.key);
    } else {
        let cart = _getCart();

        if (cartItemInfo.isExclusive) {
            await _checkQualification(cartItemInfo.sku, qty);
        }

        // Update the Quantity
        if (cart.setItemQuantity(cartItemInfo.key, qty, referror)) {
            cart.trimScanCards();
            setCart(cart);
        } else {
            throw new NsQuantityNotAvailable('');
        }
    }
}

function removeItemBySku(sku) {
    if (sku.length == '8') {
        let cart = _getCart();
        let cartItems = cart.getItems({all: true});
        for (let i = 0; i < cartItems.length; i++) {
            if (cartItems[i].product.sku === sku) {
                cart.removeProduct(cartItems[i].key);
                break;
            }
        }
        cart.trimScanCards();
        setCart(cart);
    }
}

function removeItemByKey(itemKey) {
    let cart = _getCart();

    cart.removeProduct(itemKey);
    cart.trimScanCards();
    setCart(cart);
}

function removeItems(options = {all: true}) {
    _getCart().removeItems(options);
}

function toggleItemAdrStatus(itemKey, syncAll) {
    let cart = _getCart(),
        makeAdr = cart.toggleToAdr(itemKey);

    if (syncAll) {
        setItemAdrFlags(makeAdr);
    } else {
        cart.trimScanCards();
        setCart(cart);
    }
}

function getOrderSkuQueryParams(queryParamsExist) {
    return _getCart().getOrderSkuQueryParams(queryParamsExist);
}

function isBusinessPortfolioAdr() {
    return _getCart().isBusinessPortfolioAdr();
}

function setBusinessPortfolioAdr(adrVal) {
    return _getCart().setBusinessPortfolioAdr(adrVal);
}

function setItemAdrFlags(makeAdr) {
    let cart = _getCart();

    cart.getItems({cartItems: true}).forEach(cartItem => {
        if (cartItem.adr != makeAdr && cartItem.canToggleAdr()) {
            cart.toggleToAdr(cartItem.key);
        }
    });
    cart.trimScanCards();
    setCart(cart);
}

function changeBpItem(altBpSku) {
    let cart = _getCart();

    cart.replaceBpWithAltBp(altBpSku);
    setCart(cart);
}

function cloneCart(cart) {
    // Clone the cart, so that we are not modifying the original
    return new Cart(cart.toJSON());
}

function adrHasAgelocMe() {
    let items = _getCart().getItems({cartItems: true});
    for (let i = 0; i < items.length; i++) {
        if (items[i].isAgelocme() && items[i].adr) {
            return true;
        }
    }
    return false;
}

function setSignupMode() {
    let runConfig = RunConfigService.getRunConfig(null),
        user = UserService.getUser();

    // check for signup mode
    if (runConfig.noSignupMode !== true && user && AccountManager.isLoggedIn() != null) {
        if (_getCart().checkForSignup) {
            if (user.countryCd == runConfig.country) {
                _getUserAddedItemSkus(user.id, user.eid, runConfig.country);
            }
        }
    }
}

function inSignupMode() {
    let cart = _getCart();

    return cart ? cart.inSignup : false;
}

function minAdrPsvMet() {
    let retVal = true,
        cart = _getCart(),
        nsConfig = ConfigService.getMarketConfig(),
        // ADR one-time ship-immediate order uses ADR Override mode for UI behavior,
        // but is treated like standard order for minimum PSV requirements
        cartItemTypes = (AdrService.isAdrOverride() && !AdrService.isAdrOneTimeOrder()) ? {cartItems: true} : {cartAdrItems: true},
        cartPsv = AdrService.isAdrOverride() ? cart.totalOrderPsv : cart.totalAdrPsv;

    if (cart.hasItems(cartItemTypes)) {
        retVal = nsConfig.minAdrPsv <= cartPsv || _haveOverrideSku(cart.getItems(cartItemTypes), nsConfig.minPsvAdrOverrideSkus);
    }
    return retVal;
}

function checkProductLanguages() {
    let runConfig = RunConfigService.getRunConfig(null),
        options = {
            cntryCd: runConfig.country,
            language: runConfig.language
        };

    _getCart().getItems({cartItems: true}).forEach(_cartItem => {
        let cartItem = _cartItem;

        if (runConfig.language !== cartItem.product.lang) {
            options.sku = cartItem.isAgelocme()
                ? ConfigService.getMarketConfig().agelocme.sku
                : cartItem.product.sku;
            options.priceType = cartItem.product.priceType;
            ProductService.getProductBySku(options)
                .then(response => {
                    if (response.success) {
                        let cart = _getCart();

                        cart.changeProduct(cartItem.key, response.product);
                        setCart(cart);
                    }
                });
        }
    });
}

function scanQualifiedCnt() {
    return _getCart().scanQualifiedCnt();
}

function getScanCards() {
    return _getCart().scanCards;
}

function removeScanCard(cardNumber) {
    let cart = _getCart();

    cart.removeScanCard(cardNumber);
    setCart(cart);
    return getScanCards();
}

function hasScanCardNumber(cardNumber) {
    return _getCart().hasScanCard(cardNumber);
}

function addScanCard(scanCard) {
    if (scanCard instanceof ScanCard) {
        let cart = _getCart();

        cart.addScanCard(scanCard);
        setCart(cart);
    }
}


function updateItemPointsPrice(sapItemKey, orderItemKey, qtyRedeem) {
    let cart = _getCart(),
        sapCartItem = cart.getItemByKey(sapItemKey),
        orderCartItem = cart.getItemByKey(orderItemKey),
        deltaItemTotal = 0;

    if (sapCartItem) {
        let itemPrice = sapCartItem.getQty() === qtyRedeem ? 0 : orderCartItem.product.getPrice(),
            itemTotal = itemPrice * (sapCartItem.qty - qtyRedeem),
            oldItemTotal;

        // adjustSapItemPv(sapItem, qtyRedeem);
        sapCartItem.product.pv = getItemUnitPsv(sapItemKey) * (sapCartItem.getQty() - qtyRedeem);
        sapCartItem.qtyRedeemWithPoints = qtyRedeem;

        oldItemTotal = sapCartItem.product.getPrice() || 0;  // price might be undefined
        deltaItemTotal = itemTotal - oldItemTotal;

        sapCartItem.product.setPrice(itemTotal);
        sapCartItem.redeem = qtyRedeem > 0 ? true : false;
        cart.addCartItem(sapCartItem, true);
        setCart(cart);
        syncCartItemToSapItem(sapItemKey, orderItemKey);
    }
    return {deltaItemTotal, sapItemData: sapCartItem.toFlatJson()};
}

function resetPointsAllocations() {

}

function getItemUnitPsv(sapItemKey) {
    let cart = _getCart(),
        sapItem = cart.getItemByKey(sapItemKey),
        unitPsv = 0;

    if (sapItem.product.pv > 0) {
        // calculate based off of simulate. Should not be a divide by 0 since lineItem price > 0. (qty > qtyRedeemWithPoints
        unitPsv = sapItem.product.pv / (sapItem.qty - sapItem.qtyRedeemWithPoints);
    } else {
        // can't base it off of simulate, use cart unit price.
        cart.getItems({cartItems: true})
            .filter(cartItem => cartItem.getSku() === sapItem.getSku())
            .forEach(cartItem => {
                unitPsv = cartItem.getSinglePv();
            });
    }

    return unitPsv;
}

function setCartString(adrOrder, shipImmediate) {
    _getCart().setCartString(adrOrder, shipImmediate);
}

/**
 * This will sync a cartItem from an sapItem.  Currently only a couple of
 *  redeem fields are synced.
 *
 * @param sapItemKey
 * @param cartItemKey
 */
function syncCartItemToSapItem(sapItemKey, cartItemKey = null) {
    let cart = _getCart(),
        sapItem = cart.getItemByKey(sapItemKey),
        cartItem = cartItemKey ? cart.getItemByKey(cartItemKey) : null;

    if (sapItem) {
        if (!cartItem) {
            cart.getItems({cartItems: true})
                .filter(cartItem => cartItem.getSku() === sapItem.getSku())
                .forEach(_cartItem => {
                    cartItem = _cartItem;
                });
        }
        cartItem.redeem = sapItem.redeem;
        cartItem.qtyRedeemWithPoints = sapItem.qtyRedeemWithPoints;
        cart.addCartItem(cartItem, true);
        setCart(cart);
    }
}

// ------------------------------------------------------------------------------------------------
//
//        Define all private methods.
//
// ------------------------------------------------------------------------------------------------

/**
 * This presumes the sku in question is an exclusive sku
 *
 * @param sku
 * @param newQty
 * @returns {Promise<void>}
 * @private
 */
async function _checkQualification(sku, newQty) {
    let isQualified = await QualificationService.isQualified(sku, newQty);

    if (!isQualified) {
        let str = 'You are not qualified to purchase this product';
        try {
            str = await StringService.getString('exclusiveOfferMessage', 'You are not qualified to purchase this product')
        } catch (e) {
            console.log(e);
        }
        throw new NsNotQualifiedException(str);
    }
}

function _haveOverrideSku(adrCartItems, overrideSkus) {
    let hasOverrideSku = false;

    if (typeof overrideSkus !== "undefined") {
        if (typeof overrideSkus === 'string') {
            overrideSkus = [overrideSkus];
        }
        for (let j = 0; j < adrCartItems.length; j++) {
            if (overrideSkus.indexOf(adrCartItems[j].getSku()) != -1) {
                hasOverrideSku = true;
            } else {
                hasOverrideSku = false;
            }
        }
    }
    return hasOverrideSku;
}

function _getUserAddedItemSkus(userId, eid, cntryCd) {

    const onReceivedActiveEvents = activeEvents => {
        /*
        The active events list is null until it has been initialized.
        Proceed with the callback code only once the list is initialized.
         */
        activeEvents = [];
        if (!(activeEvents instanceof Array)) {
            return;
        }

        /*
        Unsubscribe to prevent this callback from executing more than once
        after valid data has been received.
         */
        events.unsubscribe(events.salesevent.INITIAL_ACTIVE_EVENTS, onReceivedActiveEvents);

        if (activeEvents.length > 0) {
            /*
            If in an active event, skip the signup requirements network
            call and proceed as though signup requirements have been met.
             */
            _addedSkusCallbackFn({
                userId: userId,
                cntryCd: cntryCd,
                isSignup: false,
                sku: null,
                otherSkus: null
            });
        } else {
            let nsConfig = ConfigService.getMarketConfig(),
                url = nsConfig.nsurl_signupRequirementService.replace('{sapId}', userId).replace('{countryCd}', cntryCd)
                    .replace('{homeMarket}', cntryCd);
            // check to see if business portfolio needs to be added

            $.ajax({
                type: "GET",
                url: url,
                data: {"eid": eid},
                headers: {'client_id': nsConfig.checkout.clientId, 'client_secret': nsConfig.checkout.clientSecret},
                success: _signupReqMetCallbackFn(userId, cntryCd, _addedSkusCallbackFn),
                // TODO
                error: function REPLACE() {
                    console.log('SIGNUP CHECK ERROR');
                }
            });
        }
    };

    /*
    Retrieve the list of active sales events. Subscribe to changes to this
    list to avoid a potential race condition where list is retrieved before
    it has been initialized.
     */
    events.getValue(events.salesevent.INITIAL_ACTIVE_EVENTS, onReceivedActiveEvents, true);
}

function _signupReqMetCallbackFn(_userId, _country, _callbackFn) {
    let userId = _userId,
        country = _country,
        callbackFn = _callbackFn;
    return function(response) {
        let sku = null,
            otherSkus = null,
            requirementsMet = false,
            signupSkus = [];

        if (response.SignupRequirements){
            if (response.SignupRequirements.requirementsMet){
                requirementsMet = response.SignupRequirements.requirementsMet;
            }
            if (response.SignupRequirements.signupSkus){
                signupSkus = response.SignupRequirements.signupSkus;
            }
        }

        if (!requirementsMet && signupSkus && signupSkus.length > 0) {
            sku = signupSkus[0];
            otherSkus = signupSkus.splice(1);
        }
        callbackFn({
            userId: userId,
            cntryCd: country,
            isSignup: !requirementsMet,
            sku: sku,
            otherSkus: otherSkus
        });
    };
}

function _addedSkusCallbackFn(response) {
    let runConfig = RunConfigService.getRunConfig(null),
        user = UserService.getUser(),
        cart = _getCart();

    cart.inSignup = response.isSignup;
    cart.checkForSignup = false;
    setCart(cart);
    if (response.sku) {
        if (response.isSignup) {
            let options = {
                isAdr: false,
                isBusinessPortfolio: true,
                userId: response.userId,
                sku: response.sku,
                otherSkus: response.otherSkus,
                cntryCd: response.cntryCd,
                language: runConfig.language,
                region: _getUserRegion(user)
            };

            ProductService.getProductBySku(options)
                .then(response => {
                    if (response.success === true) {
                        let product = response.product;

                        addProductToCart({
                            userId: options.userId,
                            cntryCd: options.cntryCd,
                            product: product,
                            qty: 1,
                            maxQty: 999,
                            adr: false,
                            isBusinessPortfolio: true
                        }).then(() => {
                            if (options.otherSkus.length > 0) {
                                for (let i = 0; i < options.otherSkus.length; i++) {
                                    ProductService.getProductBySku({
                                        sku: options.otherSkus[i],
                                        cntryCd: options.cntryCd,
                                        language: options.language
                                    }).then(response => {
                                        let cart;

                                        if (response.success === true) {
                                            cart = _getCart();
                                            cart.addAlternateBusinessPortfolio(response.product);
                                            setCart(cart);
                                        }
                                    });
                                }
                            }
                        });
                    }
                });
        }
    }
}

function _getUserRegion(user) {
    if (user && user.shipTo && user.shipTo.country === 'CA') {
        return user.shipTo.region;
    }
    return '';
}

function clearLoyaltyPoints() {
    let cart = _getCart();
    let cartUpdateObject = {};
    cartUpdateObject = {
        loyaltyRewards: false,
        loyaltyRewardPoints: 0.0,
        loyaltyAdrRewards: false,
        loyaltyAdrRewardPoints: 0.0,
        loyaltyTotalPoints: 0
    };

    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);

}

function toggleLoyaltyRewardPoints() {
    let cart = _getCart();
    let cartUpdateObject = {};

    if (cart.loyaltyRewards == true) {
        cartUpdateObject = {
            loyaltyRewards: false,
            loyaltyRewardPoints: 0.0 };
    } else {
        cartUpdateObject = {
            loyaltyRewards: true,
            loyaltyRewardPoints: 0.0,
            loyaltyAdrRewards: false,
            loyaltyAdrRewardPoints: 0.0
        };
    }
    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);
}

function updateLoyaltyPoints(points, type = 'order') {
    if (type == 'adr') {
        setCartProperties({loyaltyAdrRewardPoints: parseFloat(points)}, true);
    } else {
        setCartProperties({loyaltyRewardPoints: parseFloat(points)}, true);
    }

    _getCart().updateCartLoyalty();
}

function toggleLoyaltyRewardPointsAdr() {
    let cart = _getCart();
    let cartUpdateObject = {};

    if (cart.loyaltyAdrRewards == true) {
        cartUpdateObject = {
            loyaltyAdrRewards: false,
            loyaltyAdrRewardPoints: 0.0};
    } else {
        cartUpdateObject = {
            loyaltyAdrRewards: true,
            loyaltyAdrRewardPoints: 0.0,
            loyaltyRewards: false,
            loyaltyRewardPoints: 0.0};
    }

    cart.updateCartLoyalty(cartUpdateObject);
    setCart(cart);

}

function calculateLoyaltyOrderTotal() {
    let cart = getCartInfo();
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();
    let calcAdjustedLoyaltyTotal = 0.0;
    let currentOrderTotal = 0.0;

    // Make sure they can earn points
    if (loyaltyInfo.userTier.purchaseRatio == 0) {
        return 0;
    }

    // Get the total of the complete order
    currentOrderTotal = cart.loyaltyTotalEarnPrice;

    if (cart.loyaltyRewards == true) {
        // If you are using Loyalty points calculate with those
        calcAdjustedLoyaltyTotal = (currentOrderTotal - cart.loyaltyRewardPoints);
    } else if (cart.loyaltyAdrRewards == true) {
        // Calculate with ADR reward points
        calcAdjustedLoyaltyTotal = currentOrderTotal - cart.loyaltyAdrRewardPoints;
    } else {
        // Use full order total
        calcAdjustedLoyaltyTotal = currentOrderTotal;
    }
    return calcAdjustedLoyaltyTotal;
}

function calculateLoyaltyEarningsToNextIncrement () {
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();
    let amountOverIncrement = 0.0;
    let amountToNextIncrement = 0.0;

    // Make sure they have an increment
    if (loyaltyInfo.userTier.purchaseIncrement == 0) {
        return 0.0;
    }

    let calcAdjustedLoyaltyTotal = calculateLoyaltyOrderTotal();

    amountOverIncrement = (calcAdjustedLoyaltyTotal % loyaltyInfo.userTier.purchaseIncrement);

    amountToNextIncrement = loyaltyInfo.userTier.purchaseIncrement - amountOverIncrement;

    return amountToNextIncrement;
}


function calculateLoyaltyEarnings (orderTotal = 0) {
    let loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();
    let nsConfig = ConfigService.getMarketConfig();

    if (!loyaltyInfo || !loyaltyInfo.userTier) {
        return 0;
    }

    let currentOrderEarnings = 0.0;
    let calcAdjustedLoyaltyTotal = 0.0;
    let amountOverIncrement = 0.0;

    if (loyaltyInfo.userTier.purchaseRatio == 0) {
        return 0;
    }
    if (orderTotal != 0) {
        calcAdjustedLoyaltyTotal = orderTotal;
    } else {
        calcAdjustedLoyaltyTotal = calculateLoyaltyOrderTotal();
    }

    // Check for a minimum amount to spend to get points
    if (loyaltyInfo.userTier.minSpend > 0) {

        // If the total doesn't exceed the min spend just return 0
        if (calcAdjustedLoyaltyTotal < loyaltyInfo.userTier.minSpend) {
            return 0;
        } else {
            // There is a min spend so calculate that portion of the rewards
            currentOrderEarnings += loyaltyInfo.userTier.minSpend * loyaltyInfo.userTier.purchaseRatio;
            // Remove min spend from order total
            calcAdjustedLoyaltyTotal = calcAdjustedLoyaltyTotal - loyaltyInfo.userTier.minSpend;
        }
    }

    // If there is a purchase increment calculate the totals
    if (loyaltyInfo.userTier.purchaseIncrement > 0) {

        amountOverIncrement = (calcAdjustedLoyaltyTotal % loyaltyInfo.userTier.purchaseIncrement);
        calcAdjustedLoyaltyTotal = calcAdjustedLoyaltyTotal - amountOverIncrement;

    }

    // Add the calculated total of point values
    currentOrderEarnings += calcAdjustedLoyaltyTotal * loyaltyInfo.userTier.purchaseRatio;

    if (nsConfig.loyalty.pointsMultiplier &&
        !isNaN(nsConfig.loyalty.pointsMultiplier) &&
        nsConfig.loyalty.pointsMultiplier != "" &&
        nsConfig.loyalty.pointsMultiplier != 0) {

        currentOrderEarnings = currentOrderEarnings * nsConfig.loyalty.pointsMultiplier;
    }

    if (loyaltyInfo.user.tier && loyaltyInfo.user.tier.maximumLimitPoints && 
        loyaltyInfo.user.tier.completedLimitPoints && loyaltyInfo.user.tier.maximumLimitPoints > 0){
        // calculate max available to earn in period
        const maxTierCapEarn = loyaltyInfo.user.tier.maximumLimitPoints - loyaltyInfo.user.tier.completedLimitPoints;
        // Only allow earning to cap
        if (currentOrderEarnings > maxTierCapEarn) {
            currentOrderEarnings = maxTierCapEarn;
        }
    }

    // Return amout that is earned on this total
    const country = RunConfigService.getRunConfig().country;
    const accuracy = CurrencyService.getCurrency(country).accuracy
    return truncate(parseFloat(currentOrderEarnings), accuracy).toFixed(accuracy);
}

function getOrderLoyaltyRewardsPointsJson() {
    let cart = getCartInfo();
    let loyaltyPoints = [];
    let runConfig = RunConfigService.getRunConfig();
    let country = runConfig.country;
    let currencyCode = CurrencyService.getCurrency(country).currencyCode;

    if (cart.loyaltyRewards == true) {
        loyaltyPoints =
            [
                {
                    'Type': 'POINTS_USED',
                    'Rate': cart.loyaltyRewardPoints,
                    'Currency': currencyCode
                }
            ];
    } else if (cart.loyaltyAdrRewards == true) {
        loyaltyPoints =
            [
                {
                    'Type': 'POINTS_USED',
                    'Rate': cart.loyaltyAdrRewardPoints,
                    'Currency': currencyCode
                }
            ];
    }

    return loyaltyPoints;
}

function containsAdrItems() {
    return _getCart().containsAdrItems();
}

/**
 * Truncates the given number to the given number of decimal places
 * @param {number} x Number to truncate
 * @param {number} [digits] Number of places right of the decimal to truncate the number to
 */
function truncate(x, digits = 0) {
    return Math.trunc(x * Math.pow(10, digits)) / Math.pow(10, digits);
}

function goToCartPage(params = {}) {
    window.location = getCartPage(params);
}

function getCartPage(params = {}) {
    let page;
    const queryParams = typeof params.queryParams === 'object' ? UrlService.toQueryString(params.queryParams) : '';
    const hashParams = params.hashParams ? `#${params.hashParams}` : '';
    const isMySite = params.isMySite || window.location.href.search(/\bmysite\b/) >= 0;

    if (ConfigService.getMarketConfig().useStaticCart) {
        const mySitePart = isMySite ? '/mysite' : '';
        page = `/static/cart/cart.html${queryParams ? '?' + queryParams : ''}#/${UrlService.getLocale()}${mySitePart}/cart${hashParams}`;
    } else {
        const mySitePart = isMySite ? '.mysite' : '';
        page = `${UrlService.getAemLocaleUrl()}cart${mySitePart}.html${queryParams ? '?' + queryParams : ''}#/cart${hashParams}`;
    }

    return page;
}

function goToCheckoutPage(params = {route: 'orderSummary'}) {
    window.location = getCheckoutPage(params);
}

function getCheckoutPage(params = {}) {
    const isMySite = params.isMySite || window.location.href.search(/\bmysite\b/) >= 0;

    return ConfigService.getMarketConfig().useStaticCheckout
        ? `/static/checkout/checkout.html#/${UrlService.getLocale()}${isMySite ? '/mysite' : ''}/${params.route}`
        : `${UrlService.getAemLocaleUrl()}checkout${isMySite ? '.mysite' : ''}.html#/${params.route}`;
}

$(function() {
    if (nuskin && nuskin.account) {
        events.subscribe(events.authentication.LOGOUT, function () {
            clearCart();
            if (typeof AdrEditService !== 'undefined') {
                AdrEditService.clearAdrEdit();
            }
        });
        events.subscribe(events.authentication.LOGIN, function() {
            let user = UserService.getUser();
            if (user) {
                let cart = _getCart();
                cart.userId = user.id;
                setCart(cart);
            }
        });
        events.getValue(events.shop.PERSONAL_OFFER_LOADED, function(personalOffer) {
            if (personalOffer) {
                let cart = _getCart();

                if (!cart.isPitchCart) {
                    cart = clearCart();
                }
                cart.isPitchCart = true;
                setCart(cart);
            }
        }, true);
    }

    events.getValue(events.salesevent.ACTIVE_EVENTS, (activeEvents) => _getCart().updateEventPrices(activeEvents), true);
    events.publish(events.salesevent.POLL_MARKET_CONFIG);
});
